import { Component, Input, OnChanges, OnDestroy, OnInit, SimpleChanges } from '@angular/core';
// import { Router } from '@angular/router';
import { WalletService } from '@app/@shared/services/wallet.service';
import { AlertController, PopoverController, ToastController } from '@ionic/angular';
import { Subscription } from 'rxjs';
import { WalletCardComponent } from '../wallet-card/wallet-card.component';
import { Credentials } from '@app/auth/credentials.service';
import { Timestamp } from 'firebase/firestore';
import { CustomerService } from '@app/@shared/services/customer.service';
import { Logger } from '@app/@shared/logger.service';

const log = new Logger('wallet-btn-component');

@Component({
  selector: 'app-wallet-btn',
  templateUrl: './wallet-btn.component.html',
  styleUrl: './wallet-btn.component.scss',
})
export class WalletBtnComponent implements OnInit, OnChanges, OnDestroy {
  @Input() color: string = 'primary'; // Default color
  @Input() alert: boolean = false; // Default to false
  @Input() walletS!: WalletService;
  @Input() credentials!: Credentials | null;
  balance = 0;
  walletId = '';
  subscriptions: Subscription[] = [];
  isLoading = false;
  email = '';
  private balanceSubscription?: Subscription;

  constructor(
    private alertController: AlertController,
    private toastCtrl: ToastController,
    private popoverCtrl: PopoverController,
    private customerS: CustomerService
  ) {}

  ngOnInit(): void {
    // Suscripción al EventEmitter para cambios de balance
    this.subscriptions.push(this.walletS.balanceChange.subscribe((b) => (this.balance = b)));

    // Inicializar suscripción si las credenciales ya están disponibles
    this.setupBalanceSubscription();
  }

  ngOnChanges(changes: SimpleChanges): void {
    // Si las credenciales cambian, actualizar la suscripción
    if (changes['credentials'] && !changes['credentials'].firstChange) {
      this.setupBalanceSubscription();
    }

    // Si el walletService cambia, reinicializar
    if (changes['walletS'] && !changes['walletS'].firstChange) {
      // Limpiar suscripciones anteriores
      this.cleanupBalanceSubscription();
      // Re-suscribirse al EventEmitter
      this.subscriptions = [];
      this.subscriptions.push(this.walletS.balanceChange.subscribe((b) => (this.balance = b)));
      this.setupBalanceSubscription();
    }
  }

  private setupBalanceSubscription(): void {
    // Limpiar suscripción anterior si existe
    this.cleanupBalanceSubscription();

    // Suscripción en tiempo real a las wallets
    if (this.credentials?.id && this.walletS) {
      this.isLoading = true;
      this.balanceSubscription = this.walletS.subscribeToBalance(this.credentials.id).subscribe({
        next: ({ balance, email }) => {
          this.balance = balance;
          this.email = email;
          this.isLoading = false;
        },
        error: (error) => {
          if (!this.credentials || !this.credentials.id) {
            // Es un error normal por perder permisos al cerrar sesión
            log.info('Suscripción de balance finalizada por cierre de sesión');
            this.balance = 0;
            this.isLoading = false;
            return;
          }
          log.error('Error en suscripción de balance:', error);
          this.isLoading = false;
          // Fallback a método tradicional si hay error
          this.getBalance();
        },
      });
      this.subscriptions.push(this.balanceSubscription);
    } else if (this.walletS && this.credentials) {
      // Si hay credenciales pero no tienen id (raro) o para método tradicional
      this.getBalance();
    } else {
      this.balance = 0;
      this.email = '';
    }
  }

  private cleanupBalanceSubscription(): void {
    if (this.balanceSubscription) {
      // Verificar que la suscripción no esté ya cerrada antes de desuscribirse
      if (!this.balanceSubscription.closed) {
        this.balanceSubscription.unsubscribe();
      }
      const index = this.subscriptions.indexOf(this.balanceSubscription);
      if (index > -1) {
        this.subscriptions.splice(index, 1);
      }
      this.balanceSubscription = undefined;
    }
  }

  ngOnDestroy(): void {
    // Limpiar la suscripción de balance específica primero
    this.cleanupBalanceSubscription();

    // Limpiar todas las demás suscripciones
    this.subscriptions.forEach((s) => {
      if (s && !s.closed) {
        s.unsubscribe();
      }
    });

    // Limpiar el array
    this.subscriptions = [];
  }

  async getBalance() {
    this.isLoading = true;
    try {
      const { balance, email } = await this.walletS.getBalance();
      this.balance = balance;
      this.email = email;
    } catch (error) {
      log.error('Error al obtener balance:', error);
    } finally {
      this.isLoading = false;
    }
  }

  getFormattedBalance(): string {
    const value = this.balance;

    if (value >= 10000) {
      // Aplica formato solo si el balance es >= 10,000
      if (value >= 1e6) {
        return (value / 1e6).toFixed(1).replace(/\.0$/, '') + 'M';
      } else if (value >= 1e3) {
        return (value / 1e3).toFixed(1).replace(/\.0$/, '') + 'K';
      }
    }

    // Para valores menores a 10,000, se devuelve con separador de miles
    return new Intl.NumberFormat('es-MX').format(value);
  }

  async openCard(ev: Event) {
    if (this.credentials?.customer.exclusion) {
      const customer = this.credentials?.customer;
      const timeEx = customer.timeEx;
      const periodEx = customer.periodEx as Timestamp; // Firestore Timestamp

      let formattedDate = '';

      // Convierte el Timestamp a Date
      const dateObj = periodEx.toDate();
      const today = new Date(); // Fecha actual

      // Formatear la fecha como "día de mes de año"
      formattedDate = dateObj.toLocaleDateString('es-ES', {
        day: '2-digit',
        month: 'long',
        year: 'numeric',
      });

      if (today < dateObj) {
        // Mensaje dinámico según timeEx
        let message = '';
        if (timeEx === '0') {
          message =
            'Tu cuenta está actualmente en autoexclusión y no puedes cargar saldo por tiempo indefinido. Si quieres reactivar tu acceso contacta a atencion_clientes@cmillonario.mx.';
        } else {
          message = `Tu cuenta está actualmente en autoexclusión y no puedes cargar saldo por ${timeEx} meses, hasta el día ${formattedDate}.`;
        }

        const alert = await this.alertController.create({
          header: 'Autoexclusión activa',
          message,
          buttons: [
            {
              text: 'Aceptar',
              role: 'cancel',
            },
          ],
          cssClass: 'alert',
        });

        await alert.present();
      } else {
        if (this.isLoading) {
          await this.customerS.update(this.credentials.id, { exclusion: false });
          this.credentials.customer.exclusion = false;
          const toast = await this.toastCtrl.create({
            message: 'Espere un momento por favor...',
            position: 'top',
            buttons: [{ icon: 'close' }],
            duration: 3000,
          });

          await toast.present();
          return;
        }
        const popover = await this.popoverCtrl.create({
          component: WalletCardComponent,
          event: ev,
          side: 'bottom',
          alignment: 'end',
          componentProps: {
            balance: this.balance,
            isPopover: true,
            walletId: this.walletId,
            walletS: this.walletS,
            email: this.email,
            credentials: this.credentials,
          },
        });

        await popover.present();
      }
    } else {
      if (this.isLoading) {
        const toast = await this.toastCtrl.create({
          message: 'Espere un momento por favor...',
          position: 'top',
          buttons: [{ icon: 'close' }],
          duration: 3000,
        });

        await toast.present();
        return;
      }
      const popover = await this.popoverCtrl.create({
        component: WalletCardComponent,
        event: ev,
        side: 'bottom',
        alignment: 'end',
        componentProps: {
          balance: this.balance,
          isPopover: true,
          walletId: this.walletId,
          walletS: this.walletS,
          email: this.email,
          credentials: this.credentials,
        },
      });

      await popover.present();
    }
  }
}
